import * as dateFns from 'date-fns'
import * as React from 'react'

import colors from '../../../../../config/colors'
import { encodeQueryString } from '../../../app/queryString'
import { Button, Icon } from '../../components'
import { TIMEFRAME } from '../../constants'
import {
  Earning,
  EarningReportReaction,
  EarningValueProperty,
  EarningsEstimateType,
  PriceReactionWhen,
} from '../earnings-common/types'
import { isAftermarket } from '../earnings-common/utils'
import { RsiValue } from './RsiValue'
import { EARNINGS_DATE_FORMAT, EarningsColor, FISCAL_YEAR_MARK } from './constants'
import { PriceReactionDataEnum, PriceReactionRowDataType } from './types'

export function getEarningsColor(color: EarningsColor): string {
  switch (color) {
    case EarningsColor.cursor:
      return FinvizSettings.hasDarkTheme ? colors.gray[200] : colors.gray[500]
    case EarningsColor.downBar:
      return colors.red[400]
    case EarningsColor.downRevision:
      return colors.red[500]
    case EarningsColor.estimate:
      return colors.amber[300]
    case EarningsColor.lowHigh:
      return FinvizSettings.hasDarkTheme ? colors.orange[500] : colors.amber[100]
    case EarningsColor.price:
      return colors.blue[400]
    case EarningsColor.upBar:
      return colors.green[400]
    case EarningsColor.upRevision:
      return colors.green[500]
    default:
      return FinvizSettings.hasDarkTheme ? colors.white : colors.black
  }
}

export function getIsEarningEstimate(
  earning: Pick<
    Earning,
    EarningValueProperty.SalesActual | EarningValueProperty.EpsReportedActual | EarningValueProperty.EpsActual
  >
) {
  const {
    [EarningValueProperty.SalesActual]: salesActual,
    [EarningValueProperty.EpsReportedActual]: epsReportedActual,
    [EarningValueProperty.EpsActual]: epsActual,
  } = earning

  return [salesActual, epsReportedActual, epsActual].every((value) => value === null)
}

function getChartLink(ticker: string, chartStartDate: Date, chartEndDate: Date) {
  // see: custom-date-range-utils.ts: getTimeframeFromDateRange
  const range = `range_${dateFns.format(chartStartDate, 'MM-dd-yyyy')}x${dateFns.format(chartEndDate, 'MM-dd-yyyy')}`
  const anchorDate = new Date()
  const startToReferenceDateNumOfDays = dateFns.differenceInCalendarDays(anchorDate, chartStartDate) + 1
  const timeframe = startToReferenceDateNumOfDays <= 200 ? TIMEFRAME.i30 : TIMEFRAME.d
  return `/charts?${encodeQueryString({
    t: ticker,
    p: timeframe,
    r: range,
    l: '1h1v',
  })}`
}

export function mapPriceReactionData(priceReactionData: EarningReportReaction[]) {
  const result: PriceReactionRowDataType[] = []

  for (const reportReaction of priceReactionData) {
    const earningsDate = dateFns.parseISO(reportReaction.reportDate)
    const fiscalEndDate = reportReaction.fiscalEndDate ? dateFns.parseISO(reportReaction.fiscalEndDate) : null

    const data: PriceReactionRowDataType = {
      label: reportReaction.reportDate,
      labelElement: (
        <div className="flex items-center text-default font-medium">
          <div className="pr-0.5">
            {dateFns.format(earningsDate, EARNINGS_DATE_FORMAT)} {isAftermarket(earningsDate) ? 'AMC' : 'BMO'}
          </div>
          <Button
            size="xsmall"
            padding="xxsmall"
            theme="transparent"
            as="a"
            href={getChartLink(
              reportReaction.ticker,
              dateFns.parseISO(reportReaction.chartStartDate),
              dateFns.parseISO(reportReaction.chartEndDate)
            )}
            target="_blank"
          >
            <Icon width={16} name="chartBar" className="text-blue-500 dark:text-blue-300" />
          </Button>
        </div>
      ),
      id: reportReaction.fiscalPeriod,
      values: Object.keys(reportReaction.reactions).map((when) => ({
        year: when,
        when: when as PriceReactionWhen,
        value: reportReaction.reactions[when].price,
      })),
      subData: [
        {
          id: PriceReactionDataEnum.priceDiff,
          label: '',
          labelElement: (
            <div className="flex w-full justify-between">
              <div>
                {formatQuarterName(reportReaction.fiscalPeriod)}
                {fiscalEndDate && <span> ({dateFns.format(fiscalEndDate, 'MMM ‘yy')})</span>}
              </div>
              <div>{reportReaction.ticker} %</div>
            </div>
          ),
          values: Object.keys(reportReaction.reactions).map((when) => ({
            year: when,
            when: when as PriceReactionWhen,
            value: reportReaction.reactions[when].priceDiff,
          })),
        },
        {
          id: PriceReactionDataEnum.spyPriceDiff,
          label: '',
          labelElement: (
            <div className="flex w-full justify-between text-3xs">
              <div className="flex items-center">
                <div className="mr-1">RSI</div>
                <RsiValue className="leading-3" value={reportReaction.rsi}>
                  {reportReaction.rsi && Math.round(reportReaction.rsi)}
                </RsiValue>
              </div>
              <div>SPY %</div>
            </div>
          ),
          values: Object.keys(reportReaction.reactions).map((when) => ({
            year: when,
            when: when as PriceReactionWhen,
            value: reportReaction.reactions[when].spyPriceDiff,
          })),
        },
      ],
    }

    result.push(data)
  }

  return result
}

const QUARTAL_DELIMITER = 'Q'
const SEMI_ANNUAL_DELIMITER = 'S'

export function getIsSemiAnnualEarning(earnings: Earning[]) {
  return !!earnings[0]?.fiscalPeriod.includes(SEMI_ANNUAL_DELIMITER)
}

export function getYearAndPeriodInt(periodString: string | null, isSemiAnnualEarning: boolean) {
  const [yearStr, periodStr] =
    periodString?.split(isSemiAnnualEarning ? SEMI_ANNUAL_DELIMITER : QUARTAL_DELIMITER) ?? []
  const year = Number.parseInt(yearStr)
  const period = Number.parseInt(periodStr)

  if (isNaN(year) || isNaN(period)) {
    return { year: null, period: null }
  }

  return { year, period }
}

export function getFormattedPeriodValue(periodString: string | null, isSemiAnnualEarning: boolean) {
  const { year, period } = getYearAndPeriodInt(periodString, isSemiAnnualEarning)

  if (year === null || period === null) {
    return null
  }

  return `${isSemiAnnualEarning ? SEMI_ANNUAL_DELIMITER : QUARTAL_DELIMITER}${period} ${year}`
}

export function formatQuarterName(fiscalPeriod: string) {
  return `${fiscalPeriod.substring(4, 6)} ‘${fiscalPeriod.substring(2, 4)}`
}

export function formatYearName(fiscalPeriod: string) {
  return `FY ‘${fiscalPeriod.substring(2, 4)}`
}

export function formatFiscalName(fiscalPeriod: string) {
  return fiscalPeriod.endsWith(FISCAL_YEAR_MARK) ? formatYearName(fiscalPeriod) : formatQuarterName(fiscalPeriod)
}

export function adjustEstimate(value: number, estimateType: EarningsEstimateType) {
  return estimateType === EarningsEstimateType.sales ? value * 1_000_000 : value
}
